import { logger } from '@adornis/base/logging';
import { Float, Int } from '@adornis/baseql/baseqlTypes';
import { Arg, Entity, Field, Mutation } from '@adornis/baseql/decorators';
import { AdornisEntity, EntityData } from '@adornis/baseql/entities/adornisEntity';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity';
import { getCollection } from '@adornis/baseql/server/collections';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { goalDocs } from 'db/staticData';
import { DateTime, Duration } from 'luxon';
import { concatMap, from, lastValueFrom, toArray } from 'rxjs';
import { EventLog } from './EventLog';
import { GoalAgg } from './GoalAgg';

// Concept:
// 0. Data from Moodle API call is saved to Temp collection
// 1. Merge Task - Goal Mapping and remove unknown tasks, save to Raw collection
// 2. Remove all attempts per question prior to the most recent one
// 3. Aggregate to user-goal level, producing average task score, save to MoodleAttempts collection

// The Raw collection is needed for the maindashboard histogram to graphs with historical context

@Entity()
export class nlpRating extends AdornisEntity {
  static _class = 'nlpRating';
  static get allFields() {
    return selectionSet(() => nlpRating, 2);
  }
  // @Field(type => String) public oldLabel!: string;
  @Field(type => String) public course!: string;
  @Field(type => String) public goalId!: number;
  @Field(type => String) public nlp!: number;
}

@Entity()
export class TempMoodleAttempts extends MongoEntity {
  static _class = 'TempMoodleAttempts';
  static _collectionName = 'tempmoodleattempts';
  static get allFields() {
    return selectionSet(() => TempMoodleAttempts, 2);
  }

  @Field(type => String) public attId!: string;
  @Field(type => String) public userId!: string;
  @Field(type => String) public taskId!: string;
  @Field(type => String) public userName!: string;
  @Field(type => String) public taskTitle!: string;
  @Field(type => String) public taskQuestion!: string;
  @Field(type => String) public response!: string;
  @Field(type => Float) public score!: number;
  @Field(type => [nlpRating]) public essayLabels?: nlpRating[];
  @Field(type => DateTime) public answerDate!: DateTime;
  @Field(type => [Int]) public goalIds!: number[];
  @Field(type => String) public taskType!: string;
  @Field(type => String) public quizName!: string;
  @Field(type => String) public pseudoRealname!: string;
  @Field(type => String) public linkTaskview!: string;
  @Field(type => String) public linkAccordeon!: string;
  @Field(type => String) public courseId!: string;
  @Field(type => String) public courseName!: string;
}
@Entity()
export class RawMoodleAttempts extends MongoEntity {
  static _class = 'RawMoodleAttempts';
  static _collectionName = 'rawmoodleattempts';
  static get allFields() {
    return selectionSet(() => RawMoodleAttempts, 2);
  }

  @Field(type => String) public attId!: string;
  @Field(type => String) public userId!: string;
  @Field(type => String) public taskId!: string;
  @Field(type => String) public moodleTaskId!: string;
  @Field(type => String) public taskTitle!: string;
  @Field(type => String) public taskQuestion!: string;
  @Field(type => String) public response!: string;
  @Field(type => Float) public score!: number;
  @Field(type => [nlpRating]) public essayLabels?: nlpRating[];
  @Field(type => [nlpRating]) public nlp?: nlpRating[];
  @Field(type => DateTime) public answerDate!: DateTime;
  @Field(type => [Int]) public goalIds!: number[];
  @Field(type => String) public taskType!: string;
  @Field(type => String) public quizName!: string;
  @Field(type => String) public userName!: string;
  @Field(type => String) public pseudoRealname!: string;
  @Field(type => String) public linkTaskview!: string;
  @Field(type => String) public linkAccordeon!: string;
  @Field(type => String) public courseGroup!: 'Laptop' | 'Solarzellen';
  @Field(type => String) public courseId!: string;
  @Field(type => String) public courseName!: string;
}

@Entity()
export class MoodleAttempts extends MongoEntity {
  static _class = 'MoodleAttempts';
  static _collectionName = 'moodleattempts';
  static get allFields() {
    return selectionSet(() => MoodleAttempts, 2);
  }

  @Field(type => String) public attId!: string;
  @Field(type => String) public userId!: string;
  @Field(type => String) public taskId!: string;
  @Field(type => String) public moodleTaskId!: string;
  @Field(type => String) public taskTitle!: string;
  @Field(type => String) public taskQuestion!: string;
  @Field(type => String) public response!: string;
  @Field(type => Float) public score!: number;
  @Field(type => [nlpRating]) public nlp?: nlpRating[];
  @Field(type => DateTime) public answerDate!: DateTime;
  @Field(type => [Int]) public goalIds!: number[];
  @Field(type => String) public taskType!: string;
  @Field(type => String) public quizName!: string;
  @Field(type => String) public userName!: string;
  @Field(type => String) public pseudoRealname!: string;
  @Field(type => String) public linkTaskview!: string;
  @Field(type => String) public linkAccordeon!: string;
  @Field(type => String) public courseGroup!: 'Laptop' | 'Solarzellen';
  @Field(type => String) public courseId!: string;
  @Field(type => String) public courseName!: string;
  @Field(type => [nlpRating]) public essayLabels?: nlpRating[];

  @Mutation(type => Boolean)
  public static updateFromMoodle(@Arg('courseSelected', type => String) courseSelected: string | null | undefined) {
    console.log('requestingrequestingrequestingrequestingrequestingrequesting course', courseSelected);
    return async (gqlFields: string) => {
      await new EventLog({
        time: DateTime.now(),
        event: 'update',
        updating: true,
        info: 'Starting update for course ' + courseSelected,
      }).create();

      let response: AxiosResponse;
      try {
        response = await axios.get(
          `https://lpa.edutec.science/local/api/index.php?authtoken=716ef91efd9e8c59a5293caad53c3bf1&action=quizresults&course[]=${courseSelected}`,
        );
      } catch (e) {
        await new EventLog({ time: DateTime.now(), event: 'API faulty response', info: e });
        await new EventLog({
          time: DateTime.now(),
          event: 'update',
          updating: false,
          info: 'Error downloading data from Moodle! Update cycle cancelled!',
        }).create();
        return;
      }
      if (response?.status != 200) {
        console.error('Moodle response != 200');
        await new EventLog({ time: DateTime.now(), event: 'API faulty response', info: response });
        await new EventLog({
          time: DateTime.now(),
          event: 'update',
          updating: false,
          info: 'Error downloading data from Moodle! Https 200',
        }).create();
        return;
      }

      logger.info(
        { length: Object.values(response.data.response).length, vals: Object.values(response.data.response)[0] },
        'FIRST API - - OBJECT',
      );
      await new EventLog({
        time: DateTime.now(),
        event: 'update',
        updating: true,
        info: 'Successfully downloaded quiz data from Moodle',
      }).create();

      try {
        // 0. Save the the relevant subset of new information from the API call to the temp database
        // const collattdrop = await getCollection<MoodleAttempts>(MoodleAttempts._collectionName);
        // const collrawdrop = await getCollection<RawMoodleAttempts>(RawMoodleAttempts._collectionName);
        // const collgoaldrop = await getCollection<GoalAgg>(GoalAgg._collectionName);
        // const colltempdrop = await getCollection<TempMoodleAttempts>(TempMoodleAttempts._collectionName);
        // try {
        //   await collattdrop.deleteMany({ courseId: courseSelected });
        // } catch (e) {
        //   logger.info(e, 'irrelevant error');
        // }
        // try {
        //   await collrawdrop.deleteMany({ courseId: courseSelected });
        // } catch (e) {
        //   logger.info(e, 'irrelevant error');
        // }
        // try {
        //   await collgoaldrop.deleteMany({ courseId: courseSelected });
        // } catch (e) {
        //   logger.info(e, 'irrelevant error');
        // }
        // try {
        //   await colltempdrop.deleteMany({ courseId: courseSelected });
        // } catch (e) {
        //   logger.info(e, 'irrelevant error');
        // }

        // await new EventLog({
        //   time: DateTime.now(),
        //   event: 'update',
        //   updating: true,
        //   info: `Cleared old data for Course ${courseSelected}`,
        // }).create();

        // if (false) {
        //   const nlpquery = Object.values(response.data.response)
        //     .filter(at => at.questiontype === 'essay' && at.answer != null)
        //     .map(at => ({ text: at.answer, id: at.questionattemptid }));
        //   logger.info({ len: nlpquery.length }, 'query length');
        //   try {
        //     const a = new Date();
        //     const nlpres = await axios.post('https://api.nlp.lpa.edutec.science/list', nlpquery.slice(0, 20), {
        //       timeout: 30000,
        //     });
        //     const b = new Date();
        //     logger.info({ resp: nlpres.data.length, duration: (b - a) / 1000 }, 'rrrrrrrrrrrrrrrrrrrrrrrrrrrrES');
        //   } catch (e) {
        //     logger.info({ error: (e as AxiosError).info }, 'EEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEror');
        //   }
        // }
        logger.info({}, 'STARTING NLP QUERY');

        const input = Object.values(response.data.response as any[]).slice(0, 10000);

        //logger.info({ input }, 'INPUT witout filterwitout filterwitout filterwitout filterwitout filter');
        //enter filtered input Moodle api —> ‘response’ variable —> response.filter(r =>  rawmoodle.find(_id:r._id))
        //  —> NLP —> fill rawmoodle collection
        //logger.info({input.filter(y=)}, 'Filtered ResResResResResResResResResResResResResResResResResResResv');

        // const existingEntries = await collrawdrop.find({}, { _id: 1 }).toArray();
        // const existingIdsSet = new Set(existingEntries.map(data => data._id));
        // const newData = input.filter(item => !existingIdsSet.has(item._id));
        // logger.info(newData.value);

        // await new EventLog({
        //   time: DateTime.now(),
        //   event: 'update',
        //   updating: true,
        //   info: 'OldData' + input.map(x => JSON.stringify(x)),
        // }).create();

        // await new EventLog({
        //   time: DateTime.now(),
        //   event: 'update',
        //   updating: true,
        //   info: 'NewData' + newData.map(x => JSON.stringify(x)),
        // }).create();

        // await new EventLog({
        //   time: DateTime.now(),
        //   event: 'update',
        //   updating: true,
        //   info: 'existingEntries' + JSON.stringify(existingEntries),
        // }).create();

        const essaycount = input.reduce((a, c) => (c.questiontype === 'essay' ? a + 1 : a), 0);
        const essaycontentfulcount = input.reduce(
          (a, c) => (c.questiontype === 'essay' && c.answer != null ? a + 1 : a),
          0,
        );
        let totaltime = 0;
        let cnt = 0;

        // const textObjects = input.map((obj, index) => {
        //   return { id: index + 1, text: obj.text };
        // });

        const colltemp = await getCollection<TempMoodleAttempts>(TempMoodleAttempts._collectionName);
        const oldids = await colltemp.distinct('_id');
        logger.info({ oldids: oldids }, 'OOOOOOOOOOOOOOOOLids');
        logger.info(
          {
            length: input.length,
            lengthhhhh: input.filter(at => !oldids.includes(String(at.questionattemptid))).length,
          },
          'üüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüüü',
        );

        //DoDo: send textObjects to NLP at once instead of requesting the api for every single text
        const docs = input
          .filter(at => !oldids.includes(String(at.questionattemptid)))
          .map((at, i, arr) => async () => {
            // logger.info({ type: at.questiontype, answer: at.answer }, 'SETUP');
            var apiresp: AxiosResponse | null = null;
            var newdataunique: EntityData<nlpRating>[] | undefined = undefined;
            if (at.questiontype === 'essay' && ![undefined, null, ''].includes(at.answer)) {
              try {
                const start = Number(new Date());
                apiresp = await axios.post('https://api.nlp.lpa.edutec.science/list', [{ id: 1, text: at.answer }], {
                  timeout: 15000,
                });
                const stop = Number(new Date());
                const mappedapidata = Object.entries(
                  (apiresp?.data[0]).labels as Record<
                    | 'M.Radiant-Energy'
                    | 'M.Radiant-variable'
                    | 'M.Electric-Energy'
                    | 'T.Process'
                    | 'M.Electric-variable'
                    | 'M.Thermal-Energy'
                    | 'M.Thermal-variable'
                    | 'Constructing explanations'
                    | 'Planning investigations'
                    | 'Analyzing data'
                    | 'M1'
                    | 'M2'
                    | 'M3'
                    | 'M4'
                    | 'T1',
                    0 | 1
                  >,
                )
                  .map(label => {
                    const newgoal = goalDocs.find(
                      goal => goal.oldNLPgoals.includes(label[0]) && at.coursename.includes(goal.courseGroup),
                    );
                    if (!newgoal) {
                      logger.error(
                        { apirespdata: apiresp?.data, label: label, courseName: at.coursename },
                        "DIDN'T FIND MATCHING NEW GOAL MAPPING",
                      );
                      return { /*oldLabel: '',*/ course: '', goalId: '', nlp: '' };
                    }
                    return {
                      // oldLabel: label[0],
                      course: newgoal.courseGroup,
                      goalId: Number(newgoal.goalNumber),
                      nlp: label[1],
                    };
                  })
                  .filter(x => x.course != '')
                  .sort((a, b) => a?.goalId! - b?.goalId!) as EntityData<nlpRating>[];

                // Mapping of old to new goals is not 1:1 (fewer new goals), so average over the scores of duplicate new goals. Use the goalId-course combinations provided in goalDocs, identify all new goals that satisfy this combination, and average the nlp rating over all present ones. The course and goalId are necessarily redundant due to the first step. Lastly, if any theoretically possible goalId-course combinations are not represented by the data, remove those entries with the last filter statement.
                newdataunique = goalDocs
                  .map(newgoalcourseCombination => {
                    const matched = mappedapidata?.filter(
                      mapped =>
                        Number(newgoalcourseCombination.goalNumber) == mapped.goalId &&
                        newgoalcourseCombination.courseGroup == mapped.course,
                    );
                    if (!matched || !matched.length) return null;
                    return {
                      _class: nlpRating._class,
                      course: matched[0]!.course,
                      goalId: matched[0]!.goalId,
                      nlp: (matched.reduce((a, c) => a + c.nlp, 0) || 0) / matched?.length,
                    };
                    // oldLabel: filtered.map(x => x.oldLabel) || '',
                    // course: filtered[0]?.course || '',
                  })
                  .filter(x => x != null);

                totaltime = totaltime + (stop - start) / 1000;
                cnt = cnt + 1;
                logger.info(
                  {
                    essaycount: essaycount,
                    essaycontentfulcount: essaycontentfulcount,
                    atti: at.questionattemptid,
                    type: at.questiontype,
                    i: i,
                    of: arr.length,
                    time: (stop - start) / 1000,
                    avgtime: Number((totaltime / cnt).toFixed(3)),
                    remainingTimeEst: Duration.fromMillis(
                      (1 - i / arr.length) * essaycontentfulcount * Number((totaltime / cnt).toFixed(3)) * 1000,
                    ).toFormat('hh:mm:ss'),
                    res: newdataunique, //?.map(x => `${x.course} - ${x.goalId}: ${x.nlp} - ${x.oldLabel}`),
                  },
                  'RESPONSE',
                );
                await new EventLog({
                  time: DateTime.now(),
                  event: 'update',
                  updating: true,
                  info:
                    'Estimated remaining time: ' +
                    String(
                      Duration.fromMillis(
                        (1 - i / arr.length) * essaycontentfulcount * Number((totaltime / cnt).toFixed(3)) * 1000,
                      ).toFormat('hh:mm:ss'),
                    ),
                }).create();
              } catch (e) {
                logger.info(
                  { error: (e as AxiosError).info, text: at.answer },
                  'EEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEROR',
                );
                await new EventLog({
                  time: DateTime.now(),
                  event: 'update',
                  updating: true,
                  info: 'Could not obtain API ratings for response: ' + at.answer,
                }).create();
              }
            }
            const res = {
              _id: at.questionattemptid,
              _class: TempMoodleAttempts._class,
              answerDate: DateTime.fromFormat(at['timestamp (von quest.at.step)'], 'yyyy-MM-dd hh:mm:ss').toJSDate(),
              userId: at.user_id,
              userName: at['username'],
              pseudoRealname: at['vor- und nachname'],
              taskTitle: at.questionname,
              taskQuestion: at['task-text'],
              response: at.answer,
              score:
                at.questiontype == 'essay' && [undefined, null, ''].includes(at.answer)
                  ? 0
                  : at.questiontype === 'essay'
                  ? undefined
                  : at.points, // The essay score cannot be set here because it needs to be matched against the goalids that are joined onto the attempts via the tasks collection. Hence set the placeholder "undefined" to be removed in the first Mongo aggregation
              taskType: at.questiontype,
              quizName: at.quizname,
              linkTaskview: 'https://lpa.edutec.science/mod/quiz/review.php?attempt=' + at['quizattempid_taskview'],
              linkAccordeon:
                'https://lpa.edutec.science/mod/quiz/report.php?id=' +
                at['coursemoduleid_accordeon'] +
                '&mode=responses',
              courseId: at.courseid,
              courseName: at.coursename,
              essayLabels: newdataunique,
            };
            // logger.info(res, 'REEEEEEEEEEEEEEs');
            return res;
          });

        logger.info({ res: docs.length }, '1------------------------------------------');
        // const nlpresolved = await docs.reduce((p, fn, i) => p.then(fn), Promise.resolve());
        const nlpresolved = await lastValueFrom(
          from(docs).pipe(
            concatMap(x => x()),
            toArray(),
          ),
        );
        logger.info({}, 'DONE WITH UPDATING');
        let res1 = await colltemp.insertMany(nlpresolved);
        logger.info({}, 'DONE WITH UPDATING TEMP');

        await new EventLog({
          time: DateTime.now(),
          event: 'update',
          updating: true,
          info: 'Evaluated all freetext responses',
        }).create();

        // const coll = await getCollection(TempMoodleAttempts._collectionName);
        // const res = await coll.find({}).limit(30).toArray();
        // logger.info({ res: res }, '###################################################');

        logger.info({}, 'Proceeding with step 1');
        // 1. Add the task meta information and kick attempts for which we have no task information (taskId === null)
        let res2 = await colltemp
          .aggregate([
            {
              $match: {
                $or: [
                  { userName: { $regex: '^learner' } },
                  // { userName: { $regex: 'test' } },
                  // { userName: 'karademir' },
                ],
              },
            },
            // Add the task meta data
            { $lookup: { from: 'tasks', localField: 'taskTitle', foreignField: 'taskName', as: 'taskMeta' } },
            {
              $addFields: {
                taskId: { $first: '$taskMeta._id' },
                moodleTaskId: { $first: '$taskMeta.moodleTaskId' },
                goalIds: { $first: '$taskMeta.goalids' },
                courseGroup: { $first: '$taskMeta.courseGroup' },
              },
            },
            { $unset: 'taskMeta' },
            // { $set: { score: { $cond: { if: { $eq: ['$score', null] }, then: -1, else: '$score' } } } },
            // ^^Alt: updateMany({ score: null }, { $set: { score: 0 } })
            // Kick invalid entries
            { $match: { userId: { $ne: null }, taskId: { $ne: null } } }, // Kick the ones that couldn't be matched in the previous stage --- DISCUSS THIS!
            // Get the most recent answer for every task-user combination
            { $sort: { userId: 1, taskId: 1, answerDate: -1 } },
            { $set: { _class: RawMoodleAttempts._class } },
            // Filter the relevant goals
            {
              $set: {
                nlp: {
                  $cond: {
                    if: { $eq: ['$taskType', 'essay'] },
                    then: {
                      $filter: { input: '$essayLabels', cond: { $in: ['$$this.goalId', '$goalIds'] } },
                    },
                    else: undefined,
                  },
                },
              },
            },
            // Compute vertical score for essay questions
            {
              $set: {
                score: {
                  $cond: {
                    if: { $eq: ['$taskType', 'essay'] },
                    then: {
                      $cond: {
                        if: { $eq: ['$nlp', []] },
                        then: {
                          $cond: {
                            if: { $in: ['$response', [undefined, null, '']] },
                            then: 0,
                            else: 1,
                          },
                        },
                        else: { $avg: '$nlp.nlp' },
                      },
                    },
                    else: '$score',
                  },
                },
              },
            },
            { $out: RawMoodleAttempts._collectionName },
          ])
          .toArray();
        logger.info(res2, 'DONE WITH AGGREGATING RAW TASKS');
        await new EventLog({
          time: DateTime.now(),
          event: 'update',
          updating: true,
          info: 'Computed scoring for freetext responses',
        }).create();

        // let coll2 = await getCollection(RawMoodleAttempts._collectionName);
        // let res21 = await coll2.distinct('score');
        // .find({ taskType: { $eq: 'essay' }, response: { $ne: null }, goalIds: { $not: { $size: 0 } } })
        // .find({})
        // .limit(5)
        // .toArray();
        // logger.info({ res: res21 }, '9999999999999999999999999999999999999999999999999999999999999999999');

        // 2. Only consider the most recent attempt (35 double attempts right now)
        // Relevant for all task views; only the front bar chart needs the attempt-wise information
        const collraw = await getCollection<RawMoodleAttempts>(RawMoodleAttempts._collectionName);
        let res5 = await collraw
          .aggregate([
            {
              $match: {
                $or: [
                  { userName: { $regex: '^learner' } },
                  // { userName: { $regex: 'test' } },
                  // { userName: 'karademir' },
                ],
              },
            },
            {
              $group: {
                _id: { userId: '$userId', taskId: '$taskId' },
                moodleTaskId: { $first: '$moodleTaskId' },
                attId: { $first: '$_id' },
                _class: { $first: '$_class' },
                //score: { $first: { $round: [{ $toDouble: '$score' }, 0] } },
                taskTitle: { $first: '$taskTitle' },
                taskQuestion: { $first: '$taskQuestion' },
                response: { $first: '$response' },
                score: { $first: '$score' },
                nlp: { $first: '$nlp' },
                taskType: { $first: '$taskType' },
                quizName: { $first: '$quizName' },
                answerDate: { $first: '$answerDate' },
                linkTaskview: { $first: '$linkTaskview' },
                goalIds: { $first: '$goalIds' },
                userName: { $first: '$userName' },
                pseudoRealname: { $first: '$pseudoRealname' },
                linkAccordeon: { $first: '$linkAccordeon' },
                courseGroup: { $first: '$courseGroup' },
                courseId: { $first: '$courseId' },
                courseName: { $first: '$courseName' },
                essayLabels: { $first: '$essayLabels' },
              },
            },
            { $set: { userId: '$_id.userId', taskId: '$_id.taskId' } },
            { $set: { _id: '$attId', _class: MoodleAttempts._class } },
            // Merge needed such that the subscriptions don't blow up because $out drops the target collection!
            {
              $merge: {
                into: MoodleAttempts._collectionName,
                on: '_id',
                whenMatched: 'replace',
                whenNotMatched: 'insert',
              },
            },
          ])
          .toArray();
        logger.info(res5, 'DONE WITH AGGREGATING TASKS');
        await new EventLog({
          time: DateTime.now(),
          event: 'update',
          updating: true,
          info: 'Computed intermediary aggregation',
        }).create();
        // const coll22 = await getCollection(MoodleAttempts._collectionName);
        // const res213 = await coll22
        //   .find({ taskType: { $eq: 'essay' }, response: { $ne: null }, goalIds: { $not: { $size: 0 } } })
        //   // .find({})
        //   .limit(5)
        //   .toArray();
        // logger.info({ res: res213 }, '.........................................................................');
        // logger.info({}, 'ready for the goal aggregation');
        // 3. Pre-aggregation for goals

        const collatt = await getCollection<MoodleAttempts>(MoodleAttempts._collectionName);
        let res3 = await collatt
          .aggregate([
            {
              $match: {
                $or: [
                  { userName: { $regex: '^learner' } },
                  // { userName: { $regex: 'test' } },
                  // { userName: 'karademir' },
                ],
              },
            },
            // Unwinding by goalIds and nlp.goalId is equivalent
            // { $unwind: '$goalIds' },
            { $unset: ['goalIds', 'essayLabels'] },
            { $unwind: '$nlp' },
            { $set: { goalId: '$nlp.goalId' } },
            {
              $group: {
                _id: { goalId: '$goalId', userId: '$userId', courseId: '$courseId' }, // GoalIds are not identified by the course!
                userName: { $first: '$userName' },
                pseudoRealname: { $first: '$pseudoRealname' },
                courseGroup: { $first: '$courseGroup' },
                courseName: { $first: '$courseName' },
                // Merge score and essayscore - separate for plausbility check
                score: { $avg: '$score' }, // Essay scores are integrated in the regular score already and goal score is the horizontal average over both!
                // essayscore: { $avg: '$nlp.nlp' },
                taskIds: { $push: '$taskId' },
              },
            },
            {
              $addFields: {
                _class: GoalAgg._class,
                _id: { $concat: [{ $toString: '$_id.goalId' }, '_', '$_id.userId', '_', '$_id.courseId'] },
                goalId: { $toString: '$_id.goalId' },
                userId: '$_id.userId',
                courseId: '$_id.courseId',
                lookupId: { $concat: ['$courseGroup', '_', { $toString: '$_id.goalId' }] },
              },
            },
            {
              $lookup: {
                from: 'goals',
                localField: 'lookupId',
                foreignField: '_id',
                as: 'goalMeta',
              },
            },
            {
              $addFields: {
                // $first is way more efficient than $unwind
                goalTitle: { $first: '$goalMeta.goalTitle' },
                goalText: { $first: '$goalMeta.goalText' },
                goalLevel: { $first: '$goalMeta.goalLevel' },
                // courseCheck: { $first: '$goalMeta.courseGroup' }, // Validity of this pipeline confirmed!
              },
            },
            { $unset: ['goalMeta', 'lookupId'] }, // Again merge to preserve the subscriptions
            { $merge: { into: GoalAgg._collectionName, on: '_id', whenMatched: 'replace', whenNotMatched: 'insert' } },
          ])
          .toArray();
        logger.info(res3, 'DONE WITH AGGREGATING GOALS');
        await new EventLog({ time: DateTime.now(), event: 'update', updating: true, info: 'All done!' }).create();

        // const coll3 = await getCollection(GoalAgg._collectionName);
        // const res25 = await coll3.find({}).limit(25).toArray();
        // logger.info({ res: res25 }, '++++++++++++++++++++++++++++++++++');

        logger.info({}, 'all done');
        await new EventLog({
          time: DateTime.now(),
          event: 'update',
          updating: false,
          info: courseSelected,
        }).create();
      } catch (e) {
        new EventLog({ time: DateTime.now(), event: 'Database error', info: e });
        return;
      }
    };
  }
}
